import { useState, useCallback } from 'react';
import { QueryKey } from 'react-query';

import { ID } from '../../../../../../../types';
import {
  UniversalFilterTaskItemsValue,
  UniversalFilterTaskItemsModes,
  UniversalFilterTaskItemsFilterState,
  UniversalFilterTaskItemsOnFilterStateChange,
  UniversalFilterTaskItemsOnChange
} from '../../UniversalFilterTaskItems.types';

import { useMountEffect } from '../../../../../../../common/hooks/useMountEffect';
import { useReactQueryState } from '../../../../../../../main/common/reactQuery/hooks/useReactQueryState';

import { useUniversalFilterTaskItemsFilterState } from '../useUniversalFilterTaskItemsFilterState';

import {
  UniversalFilterTemplateModeOption,
  UniversalFilterTemplateOnModeChange
} from '../../../../helpers/UniversalFilterTemplate';

import { filtersKeys } from '../../../../../../../locales/keys';

const defaultMode = UniversalFilterTaskItemsModes.CONTAINS_ALL_OF;

const modeOptions: UniversalFilterTemplateModeOption<UniversalFilterTaskItemsModes>[] =
  [
    {
      value: UniversalFilterTaskItemsModes.CONTAINS_ALL_OF,
      i18nText: filtersKeys.modes.containsAllOf
    }
  ];

interface UniversalFilterTaskItemsOptions {
  filterStateCacheKey: QueryKey;
  filterValue?: UniversalFilterTaskItemsValue;
  onChange: UniversalFilterTaskItemsOnChange;
}

function useUniversalFilterTaskItems({
  filterStateCacheKey,
  filterValue,
  onChange
}: UniversalFilterTaskItemsOptions) {
  const { value: savedState, setValue: saveState } =
    useReactQueryState<UniversalFilterTaskItemsFilterState>(
      filterStateCacheKey
    );

  const { handleApplyFilterState, handleParseFilterValue } =
    useUniversalFilterTaskItemsFilterState({
      onChange
    });

  const [filterState, setFilterState] =
    useState<UniversalFilterTaskItemsFilterState>(
      savedState ||
        handleParseFilterValue(filterValue) || {
          mode: defaultMode,
          itemNames: []
        }
    );

  useMountEffect(() => {
    handleApplyFilterState(filterState);
  });

  const handleFilterStateChange =
    useCallback<UniversalFilterTaskItemsOnFilterStateChange>(
      (state) => {
        setFilterState(state);
        handleApplyFilterState(state);
        saveState(state);
      },
      [handleApplyFilterState, saveState]
    );

  const handleModeChange = useCallback<
    UniversalFilterTemplateOnModeChange<UniversalFilterTaskItemsModes>
  >(
    (mode) => {
      handleFilterStateChange({
        mode,
        itemNames: filterState.itemNames || []
      });
    },
    [filterState, handleFilterStateChange]
  );

  const handleTagIdsChange = useCallback<(tagIds: ID[]) => void>(
    (tagIds) => {
      handleFilterStateChange({
        mode: filterState.mode,
        itemNames: tagIds || []
      });
    },
    [filterState, handleFilterStateChange]
  );

  return {
    modeOptions,
    filterState,
    handleModeChange,
    handleTagIdsChange
  };
}

export default useUniversalFilterTaskItems;
